import { useEffect, useState, useCallback, Fragment } from 'react';
import { useStatistics, useWindowDimensions } from "./Utilities"
import { isMobile, useMobileOrientation } from 'react-device-detect';
import { getData } from '../api/dataSlice';
import { useSelector } from "react-redux"
import { Grid } from '@mui/material'
import { setActivateDashboard } from './chatbot/ChatManager';
import dayjs from 'dayjs'
import Static from "./live/Static"
import StaticWithLive from './live/StaticWithLive';
import Timelapse from "./timelapse/Timelapse"
import Archive from "./archive/Archive"
import ArchiveControls from './archive/ArchiveControls'
import AccessMonitoring from './access/AccessMonitoring'
import AccessMonitoringControls from './access/AccessMonitoringControls';
import DashboardContent from './dashboard/DashboardContent';

export default function Content({ component }) {

    const { CAMERAS, LAYOUT: { WRAP_LIMIT }, DEBUG } = window.conf
    const { isPortrait, isLandscape } = useMobileOrientation()
    const { initialFooterHeight, accessMonitoringCameraId } = useSelector(getData)
    const { contentHeight } = useWindowDimensions()
    
    const [isSideDrawerOpen, setIsSideDrawerOpen] = useState(false)
    const [paddingFooter, setPaddingFooter] = useState(0)
    const [showDashboard, setShowDashboard] = useState(false)
    const [dashboardState, setDashboardState] = useState(null)
    
    const isArchive = component === 3
    const isMySite = component === 4
    const isAccessMonitoring = component === 5
    const drawerBleeding = 22
    const initialSiderWidth = isMobile && isLandscape ? 235 : 332
    const camCount = isAccessMonitoring ? 2 : CAMERAS.length
    const manyCameras = camCount > WRAP_LIMIT
    const hasControls = isArchive || isMySite || isAccessMonitoring
    const isStatisticsLoaded = useStatistics()
    const selectedAccessMonitoringCameraId = CAMERAS.find(elem => elem?.id === accessMonitoringCameraId) ?? CAMERAS[0]
    const camerasToRender = isAccessMonitoring ? [selectedAccessMonitoringCameraId, selectedAccessMonitoringCameraId] : CAMERAS;
    const contentWidth = hasControls
        ? isMobile
            ? isLandscape
                ? isSideDrawerOpen
                    ? `calc(100% - ${initialSiderWidth}px)`
                    : `calc(100% - ${drawerBleeding}px)`
                : '100%'
            : `calc(100% - ${initialSiderWidth}px)`
        : '100%'
    const contentHeightMobile = isMobile && isPortrait ? contentHeight - initialFooterHeight : contentHeight

    // update dashboardState.dateRange directly (used by DashboardControlsMobile when DashboardControls is unmounted)
    const handleFooterDateRangeChange = useCallback((startISO, endISO) => {
        const start = dayjs(startISO)
        const end = dayjs(endISO)
        if (start.isValid() && end.isValid()) {
            setDashboardState(prev => prev ? { ...prev, dateRange: [start, end] } : prev)
        }
    }, [])

    // reset dashboard on page change, register activation callback for chatbot on MYSITE
    useEffect(() => {
        if (isMySite) {
            setActivateDashboard(() => setShowDashboard(true))
            return () => setActivateDashboard(null)
        }
        setShowDashboard(false)
    }, [component]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        isStatisticsLoaded && DEBUG && console.log("[etracker] Successfully initialized")
    }, [isStatisticsLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

    function renderComponent(camera, renderIndex) {

        switch (component) {

            // HOME
            case 0:
                return (
                    <Static
                        contentHeight={contentHeight}
                        camera={camera}
                    />
                )

            // LIVE
            case 1:
                if (camera.video) {
                    return (
                        <StaticWithLive
                            contentHeight={contentHeight}
                            camera={camera}
                        />
                    )
                } else {
                    return (
                        <Static
                            contentHeight={contentHeight}
                            camera={camera}
                        />
                    )
                }

            // TIMELAPSE
            case 2:
                return (
                    <Timelapse
                        contentHeight={contentHeight}
                        camera={camera}
                    />
                )

            // ARCHIVE & MYSITE
            case 3:
            case 4:
                return (
                    <Archive
                        contentHeight={contentHeightMobile}
                        camera={camera}
                        isSideDrawerOpen={isSideDrawerOpen}
                        paddingFooter={paddingFooter}
                    />
                )

            // ACCESS MONITORING
            case 5:
                return (
                    <AccessMonitoring
                        camera={selectedAccessMonitoringCameraId}
                        direction={renderIndex === 0 ? "entry" : "exit"}
                    />
                )

            default:
                break;
        }
    }

    return (
        <Fragment>
            {showDashboard ? (
                <DashboardContent
                    dashboardState={dashboardState}
                    contentHeight={contentHeightMobile}
                    contentWidth={contentWidth}
                />
            ) : (
                <Grid
                    container
                    columns={{
                        xs: !isMobile && isAccessMonitoring ? 2 : 1,
                        md: !isMobile && 2,
                        lg: isAccessMonitoring ? 2 : (manyCameras ? 3 : 2),
                        xl: isAccessMonitoring ? 2 : (manyCameras ? 4 : (camCount > 2 ? 3 : 2))
                    }}
                    sx={{
                        marginBottom: hasControls && isMobile && isPortrait ? `${initialFooterHeight}px` : 0,
                        width: contentWidth,
                        transition: '0.2s linear',
                        background: "black",
                        overflowX: "hidden",
                        paddingBottom: hasControls && isMobile && isPortrait ? `${paddingFooter}px` : 0,
                    }}
                >
                    {camerasToRender.map((camera, index) => (
                        <Grid
                            item
                            key={index}
                            xs={1}
                            md={camCount % 2 // amount of images is odd (e.g. 1, 3 or 5)
                                && index === camCount - 1 // image is last element of the array
                                && !manyCameras // do not extend last item for manyCameras grid
                                && !isAccessMonitoring // do not extend last item for access monitoring
                                ? 2
                                : 1
                            }
                            xl={!isAccessMonitoring && camCount > 2 ? 1 : undefined}
                            sx={{
                                backgroundColor: "black", // TODO: change background color of images/video
                                overflow: "hidden",
                                height: isAccessMonitoring && !isMobile ? contentHeight : 'auto' // ensure full height for scrolling columns
                            }}
                        >
                            {renderComponent(camera, index)}
                        </Grid>
                    ))}
                </Grid>
            )}

            {(isMySite || isArchive) && (
                <ArchiveControls
                    initialSiderWidth={initialSiderWidth}
                    onOpenSideDrawer={setIsSideDrawerOpen}
                    setPaddingFooter={setPaddingFooter}
                    drawerBleeding={drawerBleeding}
                    component={component}
                    onShowDashboard={setShowDashboard}
                    showDashboard={showDashboard}
                    onDashboardStateChange={setDashboardState}
                    dashboardState={dashboardState}
                    onFooterDateRangeChange={handleFooterDateRangeChange}
                />
            )}

            {isAccessMonitoring && (
                <AccessMonitoringControls
                    initialSiderWidth={initialSiderWidth}
                    onOpenSideDrawer={setIsSideDrawerOpen}
                    setPaddingFooter={setPaddingFooter}
                    drawerBleeding={drawerBleeding}
                />
            )}

        </Fragment>
    )
}